import { Select } from "@/components/Selects/select";
import { type Props as SelectProps, type SelectInstance } from "react-select";
import { STATES } from "@/consts/states";
import { useMemo, forwardRef } from "react";

interface StateSelectProps
  extends Pick<
    SelectProps<Option, boolean>,
    "classNames" | "inputId" | "onBlur" | "name" | "aria-invalid"
  > {
  id?: string;
  "aria-describedby"?: string;
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
}

type Option = { label: string; value: string };

export const USStateSelect = forwardRef<
  SelectInstance<Option, boolean>,
  StateSelectProps
>(
  (
    {
      value,
      onChange,
      placeholder = "Select state",
      multiple = false,
      classNames,
      inputId,
      id,
      onBlur,
      name,
      "aria-describedby": describedBy,
      "aria-invalid": invalid,
    },
    ref,
  ) => {
    const stateOptions = useMemo(() => {
      return STATES.map(([name, code]) => ({
        label: name,
        value: code,
      }));
    }, []);

    const selectedOption = useMemo(() => {
      if (multiple) {
        return stateOptions.filter(
          (option) => Array.isArray(value) && value.includes(option.value),
        );
      }
      return stateOptions.find((option) => option.value === value);
    }, [value, stateOptions, multiple]);

    return (
      <Select
        ref={ref}
        inputId={inputId ?? id}
        onBlur={onBlur}
        name={name}
        aria-errormessage={describedBy}
        aria-invalid={invalid}
        isMulti={multiple}
        value={selectedOption}
        onChange={(newValue) => {
          if (multiple) {
            const values = Array.isArray(newValue)
              ? newValue.map((opt) => opt.value)
              : [];
            onChange(values);
          } else {
            onChange((newValue as Option)?.value || "");
          }
        }}
        options={stateOptions}
        placeholder={placeholder}
        classNames={classNames}
      />
    );
  },
);

USStateSelect.displayName = "USStateSelect";
